import { safeRelativePath } from '@clockwise/shared';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { PasswordInput } from '@/components/custom/password-input';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  fetchAuthMethods,
  getSession,
  signInWithGoogle,
  signInWithPassword,
} from '@/lib/auth-client';
import { validateLoginSearch } from '@/lib/login-search';

export const Route = createFileRoute('/login')({
  validateSearch: validateLoginSearch,
  beforeLoad: async ({ search }) => {
    // Don't auto-redirect when there's an error to display.
    if (search.error) return;
    const session = await getSession();
    if (session.data) {
      const target = safeRelativePath(search.next, window.location.origin);
      throw redirect({ to: target });
    }
  },
  component: LoginPage,
});

/** The official multi-colour Google "G" — Google's branding guidelines require
 *  their mark on the button, not a generic icon or none at all. */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="block shrink-0">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

function friendlyError(code: string | undefined, description: string | undefined): string | null {
  if (!code) return null;
  if (code === 'unable_to_create_user') {
    return 'Your organization is not registered in this system. Please contact support.';
  }
  return description ?? code;
}

/**
 * Email + password, shown only when the URL asks for it (`?showPwd=true`) AND
 * the API says this environment offers it — QA and local, never production.
 * See apps/api/src/auth/password-auth.ts and lib/login-search.ts.
 *
 * Visually subordinate to the Google button on purpose: it sits below a
 * divider, in the plain field treatment, so nobody mistakes it for the way
 * they are meant to sign in.
 */
function PasswordSignIn({ onError, next }: { onError: (msg: string) => void; next?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    onError('');
    try {
      await signInWithPassword(email, password);
      // BetterAuth has already set the session cookie on this response. A full
      // load rather than a router navigate, so the app boots with the session
      // in hand — the same state a completed OAuth redirect lands in.
      window.location.assign(safeRelativePath(next, window.location.origin));
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Invalid email or password');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wide text-muted-foreground">or</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <form className="space-y-3 text-left" onSubmit={handleSubmit}>
        <div className="space-y-1.5">
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="login-password">Password</Label>
          <PasswordInput
            id="login-password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <p className="text-center text-xs text-muted-foreground">
        Password sign-in is enabled in this environment for test automation.
      </p>
    </div>
  );
}

function LoginPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    friendlyError(search.error, search.error_description),
  );
  const cleared = useRef(false);
  const [passwordEnabled, setPasswordEnabled] = useState(false);

  // Two conditions, both required. The URL has to ask (`?showPwd=true`), so a
  // QA login screen looks like production's by default; and the API has to
  // offer it, since only the API knows per environment — one web image serves
  // both QA and production. The first is presentation only; the second is the
  // real gate. Without the param we don't even ask the API.
  //
  // Until the API answers, the form stays hidden: Google-only is the safe
  // default and the production answer.
  const showPwdRequested = search.showPwd === true;
  useEffect(() => {
    if (!showPwdRequested) {
      setPasswordEnabled(false);
      return;
    }
    let active = true;
    void fetchAuthMethods().then((methods) => {
      if (active) setPasswordEnabled(methods.password);
    });
    return () => {
      active = false;
    };
  }, [showPwdRequested]);

  // Strip ?error= from the URL once shown, but preserve ?next= so it's still
  // passed to OAuth on the next sign-in attempt — and ?showPwd=, so clearing an
  // error doesn't make the password form vanish out from under the user.
  useEffect(() => {
    if (cleared.current) return;
    if (!search.error) return;
    cleared.current = true;
    void navigate({
      to: '/login',
      search: {
        ...(search.next ? { next: search.next } : {}),
        ...(search.showPwd ? { showPwd: true as const } : {}),
      },
      replace: true,
    });
  }, [search.error, search.next, search.showPwd, navigate]);

  const handleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithGoogle(search.next);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in with Google';
      setError(message);
      setIsLoading(false);
    }
  };

  // The card is white on the cream page (`--card` over `--background`), the
  // brand line is navy at 22px, and the sign-in control is an outlined button
  // rather than a blue fill: this is the one page with a single action on it, so
  // a primary fill has nothing to outrank and the Google mark should carry the
  // colour. Matches the Numera login treatment.
  return (
    <div className="grid min-h-screen place-items-center px-4 py-10">
      <div className="w-full max-w-[380px] rounded-[12px] border border-border bg-background light:bg-card px-6 py-8 text-center shadow-sm sm:px-8 sm:py-10">
        {/* Brand and the sign-in label are the only bold things on the page;
            everything else recedes to the faint grey. */}
        <h1 className="text-[22px] font-bold leading-tight tracking-[-0.01em]">Clockwise</h1>
        <p className="mt-1 text-[13px] light:text-ink-faint text-muted-foreground">
          Timesheets / Approvals / Reports
        </p>

        {error && (
          <div
            className="mt-5 rounded-[8px] px-3 py-2.5 text-left text-[13px] leading-[1.45] light:bg-status-bad light:text-status-bad-foreground bg-destructive/10 text-destructive"
            role="alert"
          >
            {error}
          </div>
        )}

        <Button
          variant="outline"
          data-cta="google"
          className="mt-6 h-auto w-full gap-2.5 py-[11px] text-sm font-bold"
          onClick={handleSignIn}
          disabled={isLoading}
        >
          <GoogleG />
          <span>{isLoading ? 'Signing in…' : 'Sign in with Google'}</span>
        </Button>

        <p className="mt-[18px] text-xs leading-[1.5] light:text-ink-faint text-muted-foreground">
          Use your existing work Google account — access is granted per email.
        </p>
        {passwordEnabled && (
          <PasswordSignIn onError={(m) => setError(m || null)} next={search.next} />
        )}
      </div>
    </div>
  );
}
